/**
 * Shared animated orbs for platform theme (login, launcher, platform admin).
 * Many orbs with varied sizes and positions for a dense, playful background.
 */
const ORB_COLORS = {
  pink: 'var(--accent)',
  cyan: 'var(--accent-2, var(--accent))',
  amber: 'var(--accent)',
  green: 'var(--good, #2F7D5B)',
} as const;

/** Pastel palette for chat window: soft pink, peach, lavender, pale green/blue */
const ORB_COLORS_PASTEL: Record<keyof typeof ORB_COLORS, string> = {
  pink: 'rgba(255, 182, 193, 0.55)',
  cyan: 'rgba(176, 224, 230, 0.5)',
  amber: 'rgba(255, 218, 185, 0.55)',
  green: 'rgba(152, 251, 152, 0.45)',
};

/** Base durations (s) – very slow; per-orb variation so they don't move in sync. Speed +10% = divide by 1.1 */
const SPEED_FACTOR = 1 / 1.1;
const FLOAT_BASE: Record<1 | 2 | 3, number> = { 1: 40, 2: 50, 3: 32 };
const BLOB_BASE: Record<1 | 2 | 3, number> = { 1: 28, 2: 32, 3: 24 };

/** Pseudo-random but deterministic offset from index (no Math.random for stable SSR) */
function vary(i: number, range: number): number {
  return (((i * 17 + i * i * 3) % 100) / 100) * range - range / 2;
}

/** ~50 orbs with strongly varied sizes (45–380px) and positions */
const ORB_CONFIG: Array<{
  size: number;
  top: string;
  left: string;
  color: keyof typeof ORB_COLORS;
  anim: 1 | 2 | 3;
}> = [
  { size: 380, top: '5%', left: '8%', color: 'pink', anim: 1 },
  { size: 280, top: '70%', left: '5%', color: 'cyan', anim: 2 },
  { size: 240, top: '40%', left: '50%', color: 'amber', anim: 3 },
  { size: 200, top: '15%', left: '75%', color: 'pink', anim: 2 },
  { size: 45, top: '82%', left: '72%', color: 'green', anim: 3 },
  { size: 160, top: '25%', left: '25%', color: 'cyan', anim: 1 },
  { size: 340, top: '55%', left: '20%', color: 'amber', anim: 2 },
  { size: 120, top: '10%', left: '45%', color: 'pink', anim: 3 },
  { size: 100, top: '85%', left: '35%', color: 'cyan', anim: 1 },
  { size: 52, top: '35%', left: '80%', color: 'green', anim: 2 },
  { size: 80, top: '60%', left: '60%', color: 'pink', anim: 3 },
  { size: 75, top: '20%', left: '60%', color: 'amber', anim: 1 },
  { size: 300, top: '75%', left: '15%', color: 'cyan', anim: 2 },
  { size: 65, top: '45%', left: '10%', color: 'green', anim: 3 },
  { size: 48, top: '5%', left: '30%', color: 'pink', anim: 1 },
  { size: 95, top: '30%', left: '70%', color: 'cyan', anim: 2 },
  { size: 110, top: '65%', left: '45%', color: 'amber', anim: 3 },
  { size: 130, top: '12%', left: '85%', color: 'green', anim: 1 },
  { size: 150, top: '88%', left: '55%', color: 'pink', anim: 2 },
  { size: 170, top: '50%', left: '75%', color: 'cyan', anim: 3 },
  { size: 190, top: '22%', left: '12%', color: 'amber', anim: 1 },
  { size: 210, top: '78%', left: '82%', color: 'green', anim: 2 },
  { size: 230, top: '38%', left: '38%', color: 'pink', anim: 3 },
  { size: 260, top: '8%', left: '55%', color: 'cyan', anim: 1 },
  { size: 270, top: '62%', left: '8%', color: 'amber', anim: 2 },
  { size: 300, top: '72%', left: '42%', color: 'green', anim: 3 },
  { size: 85, top: '42%', left: '62%', color: 'pink', anim: 1 },
  { size: 72, top: '18%', left: '18%', color: 'cyan', anim: 2 },
  { size: 68, top: '92%', left: '25%', color: 'amber', anim: 3 },
  { size: 62, top: '48%', left: '88%', color: 'green', anim: 1 },
  { size: 88, top: '14%', left: '38%', color: 'pink', anim: 2 },
  { size: 76, top: '58%', left: '78%', color: 'cyan', anim: 1 },
  { size: 92, top: '82%', left: '48%', color: 'amber', anim: 3 },
  { size: 105, top: '28%', left: '92%', color: 'green', anim: 2 },
  { size: 115, top: '6%', left: '68%', color: 'pink', anim: 1 },
  { size: 125, top: '94%', left: '8%', color: 'cyan', anim: 3 },
  { size: 135, top: '44%', left: '28%', color: 'amber', anim: 2 },
  { size: 145, top: '66%', left: '52%', color: 'green', anim: 1 },
  { size: 155, top: '16%', left: '52%', color: 'pink', anim: 3 },
  { size: 165, top: '74%', left: '28%', color: 'cyan', anim: 2 },
  { size: 175, top: '32%', left: '8%', color: 'amber', anim: 1 },
  { size: 185, top: '88%', left: '62%', color: 'green', anim: 3 },
  { size: 195, top: '52%', left: '42%', color: 'pink', anim: 2 },
  { size: 205, top: '12%', left: '22%', color: 'cyan', anim: 1 },
  { size: 215, top: '78%', left: '88%', color: 'amber', anim: 3 },
  { size: 225, top: '38%', left: '58%', color: 'green', anim: 2 },
  { size: 235, top: '62%', left: '18%', color: 'pink', anim: 1 },
  { size: 245, top: '22%', left: '78%', color: 'cyan', anim: 3 },
];

interface PlatformOrbsProps {
  /** Optional: class name for each orb (default platform-orb). */
  orbBaseClass?: string;
  /** 'pastel' uses soft pastel colors for chat window; default uses neon palette. */
  variant?: 'default' | 'pastel';
}

export function PlatformOrbs({
  orbBaseClass = 'platform-orb',
  variant = 'default',
}: PlatformOrbsProps) {
  const colorMap = variant === 'pastel' ? ORB_COLORS_PASTEL : ORB_COLORS;
  return (
    <>
      {ORB_CONFIG.map((cfg, i) => {
        const floatDuration = Math.max(25, (FLOAT_BASE[cfg.anim] + vary(i, 20)) * SPEED_FACTOR);
        const blobDuration = Math.max(18, (BLOB_BASE[cfg.anim] + vary(i, 12)) * SPEED_FACTOR);
        const delay = (i * 0.31 + (i % 5) * 2.1) % 22;
        return (
          <div
            key={i}
            className={`${orbBaseClass} ${orbBaseClass}--anim${cfg.anim}`}
            style={{
              ['--orb-size' as string]: `${cfg.size}px`,
              ['--orb-top' as string]: cfg.top,
              ['--orb-left' as string]: cfg.left,
              ['--orb-color' as string]: colorMap[cfg.color],
              ['--orb-delay' as string]: `${delay}s`,
              ['--orb-float-duration' as string]: `${floatDuration.toFixed(1)}s`,
              ['--orb-blob-duration' as string]: `${blobDuration.toFixed(1)}s`,
            }}
            aria-hidden
          />
        );
      })}
    </>
  );
}
